import {
  Box,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { ArrowBackIos, ArrowForwardIos } from "@mui/icons-material";
import { useSwipeable } from "react-swipeable";

export default function DancingWithStarsTestimonials() {
  const { t } = useTranslation("dancingstarstestimonials");
  const quotes = t("quotes", { returnObjects: true });

  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const visibleCount = isDesktop ? 2 : 1;

  const [index, setIndex] = useState(0);

  // Auto-loop every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((prev) => (prev + visibleCount) % quotes.length);
    }, 10000);
    return () => clearInterval(interval);
  }, [visibleCount, quotes.length]);

  const prev = () => {
    setIndex((prev) => (prev - visibleCount + quotes.length) % quotes.length);
  };

  const next = () => {
    setIndex((prev) => (prev + visibleCount) % quotes.length);
  };

  const swipeHandlers = useSwipeable({
    onSwipedLeft: next,
    onSwipedRight: prev,
    trackMouse: true,
  });

  const currentQuotes = [];
  for (let i = 0; i < visibleCount; i++) {
    currentQuotes.push(quotes[(index + i) % quotes.length]);
  }

  // Animation variants for wave/fade effect
  const cardVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, delay: i * 0.15 },
    }),
    exit: { opacity: 0, y: -40, transition: { duration: 0.5 } },
  };

  return (
    <Box
      {...swipeHandlers}
      sx={{
        py: { xs: 8, md: 14 },
        px: { xs: 3, md: 10 },
        background: "linear-gradient(to bottom, #0B0B0B, #1a1a1a)",
        position: "relative",
      }}
    >
      {/* Section Label */}
      <Typography
        sx={{
          fontFamily: "Inter",
          letterSpacing: "0.25em",
          textTransform: "uppercase",
          opacity: 0.6,
          mb: 6,
          color: "#FFFFFF",
          textAlign: "center",
        }}
      >
        {t("sectionLabel")}
      </Typography>

      {/* Carousel Arrows */}
      {isDesktop && (
        <>
          <IconButton
            onClick={prev}
            sx={{
              position: "absolute",
              top: "50%",
              left: 16,
              transform: "translateY(-50%)",
              color: "#fff",
              zIndex: 10,
              backgroundColor: "rgba(255,255,255,0.05)",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
            }}
          >
            <ArrowBackIos />
          </IconButton>

          <IconButton
            onClick={next}
            sx={{
              position: "absolute",
              top: "50%",
              right: 16,
              transform: "translateY(-50%)",
              color: "#fff",
              zIndex: 10,
              backgroundColor: "rgba(255,255,255,0.05)",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
            }}
          >
            <ArrowForwardIos />
          </IconButton>
        </>
      )}

      {/* Testimonials */}
      <Box
        sx={{
          display: "flex",
          gap: { xs: 4, md: 6 },
          overflow: "hidden",
          justifyContent: "center",
        }}
      >
        <AnimatePresence initial={false}>
          {currentQuotes.map((quote, idx) => (
            <motion.div
              key={quote.id}
              custom={idx}
              variants={cardVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              style={{
                flex: "0 0 100%",
                maxWidth: isDesktop ? "45%" : "100%",
                backgroundColor: "rgba(255,255,255,0.05)",
                borderRadius: 4,
                padding: "2rem",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Playfair Display",
                  fontSize: { xs: "1.1rem", md: "1.2rem" },
                  fontWeight: 600,
                  color: "#fff",
                  mb: 1.5,
                }}
              >
                {quote.name}, {quote.age}
              </Typography>

              <Typography
                sx={{
                  fontFamily: "Inter",
                  fontSize: { xs: "0.95rem", md: "1rem" },
                  lineHeight: 1.7,
                  color: "#ccc",
                }}
              >
                "{quote.quote}"
              </Typography>
            </motion.div>
          ))}
        </AnimatePresence>
      </Box>

      {/* Carousel Dots */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: 1,
          mt: 4,
        }}
      >
        {quotes.map((_, dotIdx) => {
          const isActive = dotIdx >= index && dotIdx < index + visibleCount;
          return (
            <Box
              key={dotIdx}
              onClick={() => setIndex(dotIdx)}
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: isActive ? "#fff" : "rgba(255,255,255,0.3)",
                cursor: "pointer",
              }}
            />
          );
        })}
      </Box>
    </Box>
  );
}
